import { createEffect, createSignal } from 'solid-js';
import QRCode from 'qrcode';

// 空图片的base64数据，用于错误时的占位符
export const EMPTY_PNG_DATA =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

// 二维码生成选项接口
interface QRCodeGenerationOptions {
  /** 二维码边距 */
  margin: number;
  /** 二维码宽度 */
  width: number;
  /** 错误纠正级别 */
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

// 二维码图片组件属性
interface QRCodeImageProps {
  /** 要生成二维码的URL或文本 */
  url: string;
  iconUrl?: string;
  /** 预设的二维码图片（可选） */
  presetImage?: string;
  /** 二维码生成选项 */
  options?: QRCodeGenerationOptions;
  /** 自定义CSS类名 */
  class?: string;
  /** 图片生成后的回调函数 */
  onImageGenerated?: (imageDataUrl: string) => void;
}

/**
 * 二维码图片组件
 * 根据提供的URL或文本生成二维码图片
 */
export function QRCodeImage(props: QRCodeImageProps) {
  const [imageDataUrl, setImageDataUrl] = createSignal(props.presetImage ?? '');

  createEffect(() => {
    if (!props.url) {
      setImageDataUrl(EMPTY_PNG_DATA);
      return;
    }

    const width = props.options?.width ?? 168;
    const isHighErrorCorrection = !!props.iconUrl;

    QRCode.toDataURL(
      props.url,
      {
        margin: props.options?.margin ?? 1,
        width: width,
        errorCorrectionLevel: isHighErrorCorrection ? 'H' : (props.options?.errorCorrectionLevel ?? 'L'),
      },
      (error: Error | null | undefined, dataUrl: string) => {
        if (error) {
          console.error('QR code generation failed:', error);
          setImageDataUrl(EMPTY_PNG_DATA);
          return;
        }

        setImageDataUrl(dataUrl);
        props.onImageGenerated?.(dataUrl);
      }
    );
  });
  return (
    <Show
      when={imageDataUrl()}
      fallback={
        <div
          class="center bg-transparent"
          style={{
            width: (props.options?.width ?? 168) + 'px',
            height: (props.options?.width ?? 168) + 'px',
          }}
        >
          <BarLoading />
        </div>
      }
    >
      <div class="flex justify-center items-center relative rounded-lg">
        <img alt="Deposit QR Code" src={imageDataUrl()} class="block rounded-lg" />
        <Show when={props.iconUrl}>
          <img
            src={props.iconUrl}
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-md p-1"
            style={{ width: '22%', height: '22%' }}
            alt="Center Logo"
          />
        </Show>
      </div>
    </Show>
  );
}

/**
 * 下载文件到本地
 */
async function downloadFile(downloadUrl: string, fileName: string): Promise<void> {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', `${fileName}.png`);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * 下载图片文件
 * 支持HTTP URL和Data URL两种格式
 */
async function downloadImageFile(imageUrl: string, fileName: string): Promise<void> {
  const isHttpUrl = /^https?:\/\//.test(imageUrl);

  if (isHttpUrl) {
    try {
      // 使用fetch替代axios，符合现代Web标准
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      await downloadFile(downloadUrl, fileName);

      // 清理对象URL以释放内存
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('HTTP image download failed:', error);
      throw error;
    }
  } else {
    // 对于Data URL，直接下载
    await downloadFile(imageUrl, fileName);
  }
}

// 二维码组件属性
interface QRCodeProps {
  /** 要生成二维码的URL或文本 */
  url: string;
  /** 是否将URL视为已经是二维码图片URL */
  isImageUrl?: boolean;
  iconUrl?: string;
  /** 下载文件名（不包含扩展名） */
  downloadFileName?: string;
  /** 显示文本 */
  displayText?: string;
  /** 自定义CSS类名 */
  class?: string;
  size?: number;
}

/**
 * 完整的二维码组件
 * 包含二维码显示和下载功能
 */
function Qrcode(props: QRCodeProps) {
  const isHttpImageUrl = Boolean(props.isImageUrl && /^https?:\/\//.test(props.url));
  const [generatedImageDataUrl, setGeneratedImageDataUrl] = createSignal('');

  const handleDownload = async () => {
    try {
      const imageUrl = generatedImageDataUrl() || props.url;
      const fileName = props.downloadFileName ?? `${env.host}-qrcode`;

      await downloadImageFile(imageUrl, fileName);
      toast.success(t('QR code saved successfully'));
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('Download failed, please try again'));
    }
  };

  return (
    <div
      class={cx(
        'inline-flex center relative flex-none group self-center hover:scale-110 transition-transform duration-200 ease-in-out',
        props.class
      )}
    >
      <QRCodeImage
        url={props.url}
        presetImage={isHttpImageUrl ? props.url : undefined}
        iconUrl={props.iconUrl}
        onImageGenerated={setGeneratedImageDataUrl}
        options={{ margin: 1, width: props.size ?? 168 }}
      />

      {/* <div
        class="absolute inset-0 center bg-[rgba(0,0,0,0.6)] rounded-lg
              opacity-0
              group-hover:opacity-100 
              transition-opacity duration-200 ease-out
              cursor-pointer
              z-10"
        onClick={handleDownload}
      >
        <div class="flex flex-col items-center gap-2">
          <div class="size-10 center rounded-full bg-layer5-card">
            <IconDownload class="size-5 text-neutral-main1" />
          </div>
          <span class="caption-m-medium text-white">{t('Download')}</span>
        </div>
      </div> */}
    </div>
  );
}

export default Qrcode;
